import { getParamsFromNameValue } from '@/utils/salt-item';
import {
  getSaltCacheLocaleKey,
  getSaltCacheTranslation,
  getVariantPackPhotoURLs,
} from '@/contexts/item-context-utils';
import { getValueFromConfigs } from '@/contexts/dynamic-config-utils';
import { gql } from '@apollo/client';
import { GtinSettings } from '@/utils/gtin-metadata';
import {
  MinimalVariantPack_CatalogFieldsFragment,
  MinimalVariantPack_CatalogPageQuery,
  MinimalVariantPack_CatalogPageQueryVariables,
  MinimalVariantPack_VariantPackCountQuery,
  MinimalVariantPack_VariantPackCountQueryVariables,
  MinimalVariantPack_VisibleVariantPackIDsQuery,
  MinimalVariantPack_VisibleVariantPackIDsQueryVariables,
} from '@/types';
import {
  MinimalVariantPackData,
  OfflineMinimalVariantPackAdapter,
  OfflineVisibleVariantPackIDAdapter,
  VariantPackTranslations,
} from '@/adapters/offline-mode';
import { print as printQuery } from 'graphql';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useMaybeUserContext } from '@/contexts/user-context';

// Pass a fake restaurant ID for the following reasons:
//   1. We need to provide a restaurant ID to SALT
//   2. We want to avoid writing restaurant-specific data into the shared
//      cache row, so use a fake restaurant_id instead of a real one.
const THROWAWAY_RESTAURANT_ID = '00000000-0000-0000-0000-000000000000';

export interface ScanDownloadInfo {
  count: number;
  estimatedSize: number;
}

export const MINIMAL_VARIANT_PACK_PAGE_QUERY = gql`
  fragment MinimalVariantPack_CatalogFields on variants_packs {
    external_item_id
    gtin_metadata @include(if: $skipSaltApi) {
      metadata
    }
    item {
      description
      display_name
      photo_url_list
      uuid
    }
    order_constraint_rel {
      order_constraint {
        metadata
      }
    }
    pack {
      max_unit_size
      min_unit_size
      unit
      unit_count
    }
    salt(locale: $locale, restaurantId: $restaurantUUID)
      @skip(if: $skipLiveSalt) {
      basicInformation {
        descriptions {
          name
          value
        }
        names {
          name
          value
        }
      }
      images {
        uri
      }
    }
    salt_cache @include(if: $useSaltCache) {
      description_translations
      image_urls
      name_translations
    }
    upc_list
    uuid
  }

  query MinimalVariantPack_CatalogPage(
    $locale: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $limit: Int!
    $offset: Int!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    variants_packs(
      where: {
        supplier_uuid: { _eq: $supplierUUID }
        deleted_at: { _is_null: true }
      }
      order_by: { uuid: asc }
      limit: $limit
      offset: $offset
    ) {
      ...MinimalVariantPack_CatalogFields
    }
  }
`;

const VARIANT_PACK_COUNT_QUERY = gql`
  query MinimalVariantPack_VariantPackCount($supplierUUID: uuid!) {
    variants_packs_aggregate(
      where: {
        supplier_uuid: { _eq: $supplierUUID }
        deleted_at: { _is_null: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

const VISIBLE_VARIANT_PACK_IDS_QUERY = gql`
  query MinimalVariantPack_VisibleVariantPackIDs(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    visible_variant_packs(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      variant_pack_uuid
    }
  }
`;

const PRINTED_PAGE_QUERY = printQuery(MINIMAL_VARIANT_PACK_PAGE_QUERY);

const PAGE_SIZE = 3000;

export type MinimalVariantPackProgressCallback = (
  downloaded: number,
  total: number,
) => void;

// Tracks in-flight downloads per supplier to prevent concurrent duplicates
// (Can happen if user enables multiple chats for same supplier at once)
const inFlightDownloads = new Map<string, Promise<ScanDownloadInfo>>();

const getEntryTranslations = (
  variantPack: MinimalVariantPack_CatalogFieldsFragment,
  saltCacheLocaleKey: string,
): VariantPackTranslations | null => {
  let description: string | null = null;
  let displayName: string | null = null;

  if (variantPack.salt_cache) {
    const { description_translations, name_translations } =
      variantPack.salt_cache;
    description = getSaltCacheTranslation(
      description_translations,
      saltCacheLocaleKey,
    );
    displayName = getSaltCacheTranslation(
      name_translations,
      saltCacheLocaleKey,
    );
  } else if (variantPack.salt) {
    const { descriptions, names } = variantPack.salt.basicInformation;
    description = getParamsFromNameValue(descriptions).Description ?? null;
    displayName = getParamsFromNameValue(names).Name ?? null;
  }

  if (!description && !displayName) {
    return null;
  }

  return {
    ...(description ? { description } : {}),
    ...(displayName ? { display_name: displayName } : {}),
  };
};

export const useDownloadMinimalVariantPacks = () => {
  const locale = useItemQueryLocale();
  const userContext = useMaybeUserContext();
  const supplierMap =
    userContext && 'supplierMap' in userContext
      ? userContext.supplierMap
      : null;
  const supplierDynamicConfigMap =
    userContext && 'supplierDynamicConfigMap' in userContext
      ? userContext.supplierDynamicConfigMap
      : null;

  const getSaltQueryVariables = (supplierUUID: string) => {
    const supplierConfig = supplierDynamicConfigMap?.[supplierUUID];
    const saltApiIntegrationEnabled = Boolean(
      getValueFromConfigs('salt_api_integration_enabled', supplierConfig),
    );
    const saltCacheReadEnabled = Boolean(
      getValueFromConfigs('salt_cache_read_enabled', supplierConfig),
    );
    // the cache table wins when both flags are on, the live API when only
    // salt_api_integration_enabled is, and neither otherwise
    const useSaltCache = saltApiIntegrationEnabled && saltCacheReadEnabled;

    return {
      skipLiveSalt: !saltApiIntegrationEnabled || useSaltCache,
      skipSaltApi: !saltApiIntegrationEnabled,
      useSaltCache,
    };
  };

  // used to invalidate cache if these flags or locale changes
  const getQueryKey = (supplierUUID: string) => {
    const { skipLiveSalt, useSaltCache } = getSaltQueryVariables(supplierUUID);
    const saltKey = useSaltCache
      ? `salt_cache:${locale}`
      : skipLiveSalt
        ? 'no_salt'
        : `salt:${locale}`;

    return `${PRINTED_PAGE_QUERY}\n# ${saltKey}`;
  };

  const getCatalogPage = useLazyQuery<
    MinimalVariantPack_CatalogPageQuery,
    MinimalVariantPack_CatalogPageQueryVariables
  >(MINIMAL_VARIANT_PACK_PAGE_QUERY, { fetchPolicy: 'no-cache' });
  const getVariantPackCount = useLazyQuery<
    MinimalVariantPack_VariantPackCountQuery,
    MinimalVariantPack_VariantPackCountQueryVariables
  >(VARIANT_PACK_COUNT_QUERY, { fetchPolicy: 'no-cache' });
  const getVisibleVariantPackIDs = useLazyQuery<
    MinimalVariantPack_VisibleVariantPackIDsQuery,
    MinimalVariantPack_VisibleVariantPackIDsQueryVariables
  >(VISIBLE_VARIANT_PACK_IDS_QUERY, { fetchPolicy: 'no-cache' });

  const fetchMinimalVariantPacks = async (
    supplierUUID: string,
    onProgress?: MinimalVariantPackProgressCallback,
    signal?: AbortSignal,
  ): Promise<ScanDownloadInfo> => {
    const entries: MinimalVariantPackData = [];
    let offset = 0;

    // using supplierMap off of userContext because this can get called in use-offline-download as well, which doesn't have NewOrderContextProvider
    const gtinSettings = (supplierMap?.[supplierUUID]?.supplier_metadata
      ?.gtin_settings ?? null) as GtinSettings | null;
    const saltQueryVariables = getSaltQueryVariables(supplierUUID);
    const saltCacheLocaleKey = getSaltCacheLocaleKey(locale);

    // progress percentage needed in scan screen
    let totalCount = 0;
    if (onProgress) {
      const { data: countData } = await getVariantPackCount({
        context: {
          fetchOptions: { signal },
          noBatch: true,
          queryDeduplication: false,
          ...(!signal ? { offlineSync: true } : {}), // no signal means it's being used by offline mode
        },
        variables: { supplierUUID },
      });
      totalCount = countData?.variants_packs_aggregate?.aggregate?.count ?? 0;
      onProgress(0, totalCount);
    }

    while (true) {
      const { data } = await getCatalogPage({
        context: {
          fetchOptions: { signal },
          noBatch: true,
          queryDeduplication: false,
          ...(!signal ? { offlineSync: true } : {}), // no signal means it's being used by offline mode
        },
        variables: {
          limit: PAGE_SIZE,
          locale,
          offset,
          restaurantUUID: THROWAWAY_RESTAURANT_ID,
          supplierUUID,
          ...saltQueryVariables,
        },
      });

      const variantPacks: MinimalVariantPack_CatalogFieldsFragment[] =
        data.variants_packs ?? [];
      if (variantPacks.length === 0) {
        break;
      }

      for (const vp of variantPacks) {
        const rule =
          vp.order_constraint_rel?.order_constraint.metadata?.rules?.[0];

        const maxQuantity =
          typeof rule?.max_quantity === 'number' ? rule?.max_quantity : null;
        const minQuantity =
          typeof rule?.min_quantity === 'number' ? rule?.min_quantity : null;

        // will only be used for thumbnail
        const [firstPhotoURL] = getVariantPackPhotoURLs(vp, {
          gtinSettings,
          saltApiIntegrationEnabled: !saltQueryVariables.skipSaltApi,
        });

        const translations = getEntryTranslations(vp, saltCacheLocaleKey);

        entries.push({
          description: vp.item.description ?? null,
          display_name: vp.item.display_name,
          external_item_id: vp.external_item_id ?? null,
          item_uuid: vp.item.uuid,

          // omit when null to save some space, most VPs don't have it
          ...(maxQuantity !== null ? { order_max_quantity: maxQuantity } : {}),
          ...(minQuantity !== null ? { order_min_quantity: minQuantity } : {}),
          pack_max_unit_size: vp.pack?.max_unit_size ?? 1,
          pack_min_unit_size: vp.pack?.min_unit_size ?? 1,
          pack_unit: vp.pack?.unit ?? null,
          ...(firstPhotoURL ? { photo_url: firstPhotoURL } : {}),
          ...(translations ? { translations } : {}),
          unit_count: vp.pack?.unit_count ?? null,
          upc_list: vp.upc_list ?? null,
          uuid: vp.uuid,
        });
      }

      if (onProgress) {
        onProgress(offset + variantPacks.length, totalCount);
      }

      if (variantPacks.length < PAGE_SIZE) {
        break;
      }
      offset += PAGE_SIZE;
    }

    const stringifiedEntries = JSON.stringify(entries);

    const adapter = OfflineMinimalVariantPackAdapter.getInstance(supplierUUID);
    await adapter.saveStringified(stringifiedEntries);
    await adapter.saveQueryKey(getQueryKey(supplierUUID));

    return {
      count: entries.length,
      estimatedSize: stringifiedEntries.length,
    };
  };

  // Used by the offline sync flow — deduplicates concurrent downloads
  // for the same supplier (e.g. enabling multiple chats at once)
  const downloadMinimalVariantPacks = async (
    supplierUUID: string,
  ): Promise<ScanDownloadInfo> => {
    const existingPromise = inFlightDownloads.get(supplierUUID);
    if (existingPromise) {
      return existingPromise;
    }

    const promise = fetchMinimalVariantPacks(supplierUUID);
    inFlightDownloads.set(supplierUUID, promise);
    try {
      return await promise;
    } finally {
      inFlightDownloads.delete(supplierUUID);
    }
  };

  const downloadVisibleVariantPackIDs = async (
    restaurantUUID: string,
    supplierUUID: string,
    signal?: AbortSignal,
  ): Promise<ScanDownloadInfo> => {
    const { data } = await getVisibleVariantPackIDs({
      context: {
        fetchOptions: { signal },
        noBatch: true,
        queryDeduplication: false,
        ...(!signal ? { offlineSync: true } : {}), // no signal means it's being used by offline mode
      },
      variables: { restaurantUUID, supplierUUID },
    });

    const uuids: string[] = (data.visible_variant_packs ?? []).reduce(
      (acc, { variant_pack_uuid }) => {
        if (variant_pack_uuid) {
          acc.push(variant_pack_uuid);
        }

        return acc;
      },
      [] as string[],
    );

    const stringifiedIds = JSON.stringify(uuids);

    const adapter = OfflineVisibleVariantPackIDAdapter.getInstance(
      restaurantUUID,
      supplierUUID,
    );
    await adapter.saveStringified(stringifiedIds);

    return {
      count: uuids.length,
      estimatedSize: stringifiedIds.length,
    };
  };

  return {
    downloadMinimalVariantPacks,
    downloadVisibleVariantPackIDs,
    fetchMinimalVariantPacks,
    getQueryKey,
  };
};
